import Image from 'next/image'
import useEmblaCarousel from 'embla-carousel-react'
import AutoScroll from 'embla-carousel-auto-scroll'
import { SectionHeader } from '../SectionHeader'
import type { ClientsSectionData as data } from '@/data/inc/homepage-types'

type OurClientsProps = {
  data: data
  strapiData?: unknown
}

export const OurClients = ({
  data: data,
  strapiData: _strapiData
}: OurClientsProps) => {
  const brands =
    data.brands?.length > 0
      ? data.brands.map(brand => ({
          url: brand.url,
          alternativeText: brand.name,
          width: 200, // Default width for SVGs
          height: 56 // Default height (matches maxHeight in styles)
        }))
      : []

  return (
    <div className="overflow-x-hidden">
      <SectionHeader
        label={data.label}
        icon={
          <svg
            width="18"
            height="15"
            viewBox="0 0 18 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9 14L6.33333 5.33333L7.66667 1M9 14L11.6667 5.33333L10.3333 1M9 14L1 5.33333M9 14L17 5.33333M17 5.33333H1M17 5.33333L13.8889 1.00002L4.11111 1L1 5.33333"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
          </svg>
        }
        heading={data.heading}
        color="inc-primary-10"
      />
      {brands.length > 0 ? <BrandsCarousel brands={brands} /> : null}
    </div>
  )
}

const BrandsCarousel = ({ brands }: { brands: StrapiImageFile[] }) => {
  const [emblaRef1] = useEmblaCarousel({ loop: true, dragFree: true }, [
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    AutoScroll({ playOnInit: true, speed: 1, stopOnInteraction: false }) as any
  ])

  const [emblaRef2] = useEmblaCarousel({ loop: true, dragFree: true }, [
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    AutoScroll({
      playOnInit: true,
      speed: 1,
      stopOnInteraction: false,
      direction: 'backward'
    }) as any
  ])

  // Split brands into two rows
  const midpoint = Math.ceil(brands.length / 2)
  const row1Brands = brands.slice(0, midpoint)
  const row2Brands = brands.slice(midpoint)
  const overlayBaseColor = 'var(--white, #ffffff)'
  const leftFadeStyle = {
    background: `linear-gradient(90deg, ${overlayBaseColor} 0%, rgba(255, 255, 255, 0) 100%)`
  }
  const rightFadeStyle = {
    background: `linear-gradient(270deg, ${overlayBaseColor} 0%, rgba(255, 255, 255, 0) 100%)`
  }

  return (
    <div className="relative max-w-full overflow-hidden flex flex-col gap-8 py-12">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 md:w-24"
        style={leftFadeStyle}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 md:w-24"
        style={rightFadeStyle}
      />
      <div className="overflow-hidden w-full" ref={emblaRef1}>
        <div className="flex gap-16 items-center">
          {[...row1Brands, ...row1Brands, ...row1Brands].map((brand, index) => (
            <div
              key={`row1-${index}`}
              className="flex-[0_0_auto] flex items-center justify-center"
              style={{ minWidth: '150px' }}
            >
              <Image
                src={brand.url}
                alt={brand.alternativeText ?? ''}
                height={brand.height}
                width={brand.width}
                className="object-contain hover:brightness-100 transition-all max-h-14"
                unoptimized
              />
            </div>
          ))}
        </div>
      </div>

      <div className="overflow-hidden w-full" ref={emblaRef2}>
        <div className="flex gap-16 items-center">
          {[...row2Brands, ...row2Brands, ...row2Brands].map((brand, index) => (
            <div
              key={`row2-${index}`}
              className="flex-[0_0_auto] flex items-center justify-center"
              style={{ minWidth: '150px' }}
            >
              <Image
                src={brand.url}
                alt={brand.alternativeText ?? ''}
                height={brand.height}
                width={brand.width}
                className="object-contain hover:brightness-100 transition-all max-h-14"
                unoptimized
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
